import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { CheckCircle2, LogIn, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { SignaturePad } from "@/components/signature-pad";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ApiError, apiFetch } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { createResource } from "@/lib/master-data";

interface ConfirmationInfo {
  purpose: "handover" | "return";
  targetId: string;
  status: string;
  expiresAt: string;
  isRecipient: boolean;
  recipientName: string;
  title: string;
  summary: string;
  loginRequired: boolean;
}

export function ConfirmPage() {
  const { token } = useParams({ from: "/confirm/$token" });
  const navigate = useNavigate();
  const me = useMe();
  const [reason, setReason] = useState("");
  const [confirmationText, setConfirmationText] = useState(
    "本人已核对设备、附件与交接条件，确认接收。",
  );
  const [done, setDone] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const info = useQuery({
    queryKey: ["public-confirmation", token],
    queryFn: () => apiFetch<ConfirmationInfo>(`/api/v1/public/confirmations/${token}`),
    retry: false,
  });

  function goLogin() {
    sessionStorage.setItem("edm_confirm_token", token);
    navigate({ to: "/login" });
  }

  async function decide(decision: "confirm" | "reject") {
    if (!info.data) return;
    if (!info.data.isRecipient) {
      toast.error("当前登录账号不是指定接收人");
      return;
    }
    setBusy(true);
    try {
      if (info.data.purpose === "handover") {
        await createResource(`/api/v1/handovers/${info.data.targetId}/decide`, {
          decision,
          confirmationText: decision === "confirm" ? confirmationText : null,
          rejectedReason: decision === "reject" ? reason : null,
          signatureData: decision === "confirm" ? signature : null,
        });
      } else {
        await createResource(`/api/v1/return-confirmations/${info.data.targetId}/decide`, {
          decision,
          reason: decision === "reject" ? reason : null,
          signatureData: decision === "confirm" ? signature : null,
        });
      }
      setDone(decision);
      toast.success(decision === "confirm" ? "已确认" : "已拒绝");
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "操作失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>一次性确认</CardTitle>
          <CardDescription>
            链接仅用于本次确认；登录后由指定接收人操作，过期或已使用后失效。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {info.isLoading ? (
            <p className="text-sm text-muted-foreground">正在读取确认信息…</p>
          ) : null}
          {info.error ? (
            <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
              {info.error instanceof ApiError ? info.error.message : "确认链接无效"}
            </p>
          ) : null}
          {info.data ? (
            <>
              <div className="rounded-lg border border-border p-3">
                <p className="font-medium">{info.data.title}</p>
                <p className="mt-1 text-sm text-muted-foreground">对象：{info.data.summary}</p>
                <p className="text-sm text-muted-foreground">接收人：{info.data.recipientName}</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  截止：{new Date(info.data.expiresAt).toLocaleString("zh-CN")} · 状态：
                  <Badge variant={info.data.status === "pending" ? "warning" : "secondary"}>
                    {info.data.status}
                  </Badge>
                </p>
              </div>

              {!me.isLoading && !me.data ? (
                <Button className="w-full" onClick={goLogin}>
                  <LogIn className="h-4 w-4" />
                  登录后确认
                </Button>
              ) : null}
              {me.data && !info.data.isRecipient ? (
                <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                  当前登录账号不是该链接的指定接收人。
                </p>
              ) : null}
              {me.data && info.data.isRecipient && info.data.status === "pending" && !done ? (
                <div className="space-y-3">
                  {info.data.purpose === "handover" ? (
                    <Input
                      value={confirmationText}
                      onChange={(event) => setConfirmationText(event.target.value)}
                    />
                  ) : null}
                  <Input
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="拒绝时填写原因"
                  />
                  <SignaturePad onChange={setSignature} />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => decide("reject")} disabled={busy}>
                      <XCircle className="h-4 w-4" />
                      拒绝
                    </Button>
                    <Button onClick={() => decide("confirm")} disabled={busy}>
                      <CheckCircle2 className="h-4 w-4" />
                      确认
                    </Button>
                  </div>
                </div>
              ) : null}
              {done ? (
                <p className="rounded-md bg-green-50 p-3 text-sm text-green-800">
                  {done === "confirm" ? "已确认，业务状态已更新。" : "已拒绝，业务已退回经办人。"}
                </p>
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
